import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Server, Settings2, Camera } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, ReferenceArea, Legend } from "recharts";
import { API } from "../../config";
import { InfraMonthlyReport } from "./InfraMonthlyReport";
import { InfraAlertHistory } from "./InfraAlertHistory";
import { InfraMemoryBreakdown } from "./InfraMemoryBreakdown";
import { InfraPeaksTable } from "./InfraPeaksTable";
import { infraT } from "./infraI18n";

const LEVEL = {
  green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  yellow: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  red: "bg-red-500/15 text-red-300 border-red-500/40",
  unknown: "bg-gray-500/15 text-gray-300 border-gray-500/40",
};
const gb = (b) => (b == null ? "—" : `${(b / 1024 ** 3).toFixed(1)} GB`);
const mb = (b) => (b == null ? "—" : `${(b / 1024 ** 2).toFixed(0)} MB`);
const pct = (v) => (v == null ? "—" : `${v}%`);
const hh = (ts, range, loc) => new Date(ts).toLocaleString(loc, range === "24h" ? { hour: "2-digit", minute: "2-digit" } : { day: "2-digit", month: "2-digit" });
const ticksFor = (from, to, range) => {
  const step = range === "24h" ? 3 * 3600e3 : range === "7d" ? 86400e3 : 3 * 86400e3;
  const out = [];
  for (let t = Math.ceil(from / 3600e3) * 3600e3; t <= to; t += step) out.push(t);
  return out;
};
const TimeAxis = ({ from, to, range, loc }) => (
  <XAxis dataKey="t" type="number" scale="time" domain={[from, to]} ticks={ticksFor(from, to, range)}
    tickFormatter={(v) => hh(v, range, loc)} stroke="#6b7280" tick={{ fontSize: 11 }} />
);

const Tile = ({ label, value, sub, level, testId }) => (
  <div className="bg-[#0a0a0a] border border-gray-800 rounded-xl p-4" data-testid={testId}>
    <p className="text-xs text-gray-400">{label}</p>
    <p className={`text-2xl font-semibold mt-1 ${level === "red" ? "text-red-400" : level === "yellow" ? "text-amber-300" : "text-white"}`}>{value}</p>
    <p className="text-xs text-gray-500 mt-1">{sub}</p>
  </div>
);

const SettingsForm = ({ data, headers, onSaved, t }) => {
  const [plan, setPlan] = useState(data.plan_name);
  const [alerts, setAlerts] = useState(data.email_alerts);
  const save = async () => {
    try {
      await axios.put(`${API}/admin/infra-resources/settings`, { plan_name: plan, email_alerts: alerts }, { headers });
      toast.success(t.saved); onSaved();
    } catch { toast.error(t.save_err); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3 border-t border-gray-800 pt-4" data-testid="infra-settings">
      <label className="text-xs text-gray-400">{t.plan_name}
        <input value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="infra-plan-input"
          className="block mt-1 bg-[#0a0a0a] border border-gray-700 rounded-xl px-3 py-2 text-white text-sm w-72" />
      </label>
      <label className="flex items-center gap-2 text-sm text-gray-300 pb-2">
        <input type="checkbox" checked={alerts} onChange={(e) => setAlerts(e.target.checked)} data-testid="infra-alerts-toggle" />
        {t.alerts}
      </label>
      <button onClick={save} className="btn-lojack text-sm" data-testid="infra-settings-save">{t.save}</button>
    </div>
  );
};

export const InfraResourcesPanel = ({ token, canEdit = false, compact = false, lang = "it" }) => {
  const t = infraT(lang); const loc = t.locale;
  const [expanded, setExpanded] = useState(!compact);
  const [range, setRange] = useState("24h");
  const [data, setData] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

  const load = useCallback(() => axios.get(`${API}/admin/infra-resources`, { params: { range, lang: lang === "en" ? "en" : "it" }, headers })
    .then((r) => setData(r.data)).catch(() => setData(null)), [range, token, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-gray-500 p-6" data-testid="infra-loading">{t.loading}</div>;
  const c = data.current; const rec = data.recommendation;
  const from = new Date(data.since).getTime(); const to = new Date(data.until).getTime();
  const series = data.series.map((s) => ({ ...s, t: new Date(s.at).getTime() }));
  const partial = data.first_sample_at && new Date(data.first_sample_at).getTime() > from;
  const en = lang === "en";
  const hourly = (data.hourly_peaks || []).map((p) => ({ ...p, t: new Date(p.at).getTime() }));
  const runs = (data.browser_runs || []).map((r) => ({ x1: Math.max(new Date(r.start).getTime(), from), x2: r.end ? new Date(r.end).getTime() : to }));
  const cls = LEVEL[rec.level] || LEVEL.unknown; const label = t[`lv_${rec.level}`] || t.lv_unknown;
  return (
    <div className="bg-[#111] border border-gray-800 rounded-2xl p-6 space-y-5" data-testid="infra-resources-panel">
      <div className="flex justify-between flex-wrap gap-3">
        <div>
          <p className="flex items-center gap-2 text-white font-semibold"><Server size={18} /> {lang === "en" ? `${data.provider} ${t.resources}` : `${t.resources} ${data.provider}`}</p>
          <p className="text-xs text-gray-400 mt-1" data-testid="infra-plan-name">{t.plan}: {data.plan_name} · {t.every15}{c.at ? ` · ${t.last} ${new Date(c.at).toLocaleString(loc)}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          {["24h", "7d", "30d"].map((r) => (
            <button key={r} onClick={() => setRange(r)} data-testid={`infra-range-${r}`}
              className={`px-3 py-1.5 rounded-lg text-xs border ${range === r ? "bg-white text-black border-white" : "border-gray-700 text-gray-300"}`}>
              {t[`r${r}`]}
            </button>
          ))}
          {canEdit && <button onClick={() => setShowSettings((v) => !v)} className="text-gray-400 hover:text-white ml-1" data-testid="infra-settings-btn"><Settings2 size={16} /></button>}
        </div>
      </div>
      <div className={`border rounded-xl px-4 py-3 text-sm ${cls}`} data-testid="infra-recommendation">
        <b>{label}.</b> {rec.text}
      </div>
      {data.browser_active && (
        <div className="flex items-center gap-2 border border-violet-500/40 bg-violet-500/15 text-violet-200 rounded-xl px-4 py-2 text-sm" data-testid="infra-browser-active">
          <Camera size={15} /> {en ? "Manuals headless browser running now: memory peaks in this phase are expected." : "Browser automatico dei manuali in funzione ora: i picchi di memoria in questa fase sono previsti."}
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Tile label={t.mem} value={pct(c.mem_pct)} sub={`${gb(c.mem_used)} ${t.of} ${gb(c.mem_max)} · ${t.peak} ${pct(data.peaks.mem_pct)}`} level={data.levels.mem_pct} testId="infra-tile-mem" />
        <Tile label={t.cpu} value={pct(c.cpu_pct)} sub={`${c.cpu_cores ?? "—"} ${t.cores} · ${t.peak} ${pct(data.peaks.cpu_pct)}`} level={data.levels.cpu_pct} testId="infra-tile-cpu" />
        <Tile label={t.disk} value={pct(c.disk_pct)} sub={`${gb(c.disk_used)} ${t.of} ${gb(c.disk_total)} · ${t.disk_note}`} level={data.levels.disk_pct} testId="infra-tile-disk" />
        <Tile label={t.db} value={mb(c.db_size)} sub={t.db_sub} testId="infra-tile-db" />
        <Tile label={t.rt} value={c.rt_avg_ms == null ? "—" : `${c.rt_avg_ms} ms`} sub={`${t.rt_sub} ${c.rt_p95_ms ?? "—"} ms`} testId="infra-tile-rt" />
      </div>
      {compact && (
        <button onClick={() => setExpanded((v) => !v)} className="text-xs text-gray-400 hover:text-white" data-testid="infra-toggle-charts">
          {expanded ? t.hide : t.show}
        </button>
      )}
      {expanded && <>
      {partial && (
        <p className="text-xs text-amber-300/90" data-testid="infra-partial-note">
          {t.partial.replace("{d}", new Date(data.first_sample_at).toLocaleString(loc))}
        </p>
      )}
      <div className="h-64" data-testid="infra-chart-usage">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#1f2937" vertical={false} />
            {TimeAxis({ from, to, range, loc })}
            <YAxis domain={[0, 100]} stroke="#6b7280" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
            <Tooltip labelFormatter={(v) => new Date(v).toLocaleString(loc)} contentStyle={{ background: "#000", border: "1px solid #374151", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-gray-300">{v}</span>} />
            <ReferenceLine y={data.thresholds.red} stroke="#ef4444" strokeDasharray="4 4" />
            <ReferenceLine y={data.thresholds.yellow} stroke="#f59e0b" strokeDasharray="4 4" />
            {runs.map((r, i) => <ReferenceArea key={i} x1={r.x1} x2={Math.max(r.x2, r.x1 + 600e3)} fill="#8b5cf6" fillOpacity={0.18} stroke="none" ifOverflow="hidden" />)}
            <Line dataKey="mem_pct" name={t.mem_s} stroke="#E31837" dot={false} strokeWidth={2} />
            {hourly.length > 0 && <Line data={hourly} dataKey="max_pct" name={en ? "Hourly memory peak % (30 s)" : "Picco orario memoria % (30 s)"} stroke="#fb7185" strokeDasharray="3 3" dot={false} strokeWidth={1.5} />}
            <Line dataKey="cpu_pct" name={t.cpu_s} stroke="#60a5fa" dot={false} strokeWidth={2} />
            <Line dataKey="disk_pct" name={t.disk_s} stroke="#a3a3a3" dot={false} strokeWidth={1.5} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {runs.length > 0 && (
        <p className="flex items-center gap-2 text-xs text-violet-300" data-testid="infra-browser-legend">
          <span className="inline-block w-3 h-3 rounded-sm bg-violet-500/40" /> {en ? `Manuals headless browser running (${runs.length} in the period)` : `Browser automatico dei manuali in funzione (${runs.length} nel periodo)`}
        </p>
      )}
      <div className="h-40" data-testid="infra-chart-rt">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#1f2937" vertical={false} />
            {TimeAxis({ from, to, range, loc })}
            <YAxis stroke="#6b7280" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} ms`} />
            <Tooltip labelFormatter={(v) => new Date(v).toLocaleString(loc)} contentStyle={{ background: "#000", border: "1px solid #374151", fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => <span className="text-gray-300">{v}</span>} />
            <Line dataKey="rt_avg_ms" name={t.rt_avg} stroke="#e5e7eb" dot={false} strokeWidth={2} />
            <Line dataKey="rt_p95_ms" name={t.rt_p95} stroke="#f59e0b" dot={false} strokeWidth={1.5} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      </>}
      {expanded && <InfraPeaksTable peaks={data.top_peaks || []} lang={lang} red={data.thresholds.red} />}
      {expanded && <InfraMonthlyReport headers={headers} lang={lang} />}
      {expanded && <InfraMemoryBreakdown headers={headers} lang={lang} />}
      {expanded && <InfraAlertHistory headers={headers} lang={lang} />}
      {canEdit && showSettings && <SettingsForm data={data} headers={headers} onSaved={load} t={t} />}
    </div>
  );
};
